import React, { useCallback, useEffect, useState } from 'react';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import styled from 'styled-components';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../routing/helpers/router';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import ObligationCard from './ObligationCard';
import Translator from '../../../../components/helpers/Translator';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import ObligationResourceTabEditMode from './ObligationResourceTabEditMode';
import ResourceCheckbox from './Obligation/ResourceCheckbox';
import MassiveObligationNextDateSettings from './Obligation/MassiveObligationNextDateSettings';
import MassiveEvidenceEditForm from './Obligation/MassiveEvidenceEditForm';
import _ from 'lodash';

export default function ObligationResourceTab({ elementID, view }) {
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const isAdmin = useSelector((state) => state?.auth?.user?.isAdmin);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge,
  );
  const resourceObligations = useSelector(
    (s) => s.currentPage?.resourceObligationTab?.[elementID],
  );
  const updateResourceObligationTab = useSelector(
    (s) => s.currentPage?.updateResourceObligationTab,
  );
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [obligationIDsInPlant, setObligationIDsInPlant] = useState();

  // > LOAD OBLIGATION TAB
  const loadResourceObligations = useCallback(async () => {
    return axios
      .post(axiosPath('api_r_user_module_resourceManager_resourceObligation_tab'), {
        moduleBridgeHex,
        elementID,
        view,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          if (isAdmin) setObligationIDsInPlant(data?.obligationIDsInPlant);
          dispatch(
            currentPageAction.change('resourceObligationTab', {
              [elementID]: data?.resourceObligations,
            }),
          );
          dispatch(
            currentPageAction.change('updateResourceObligationTab', null, true),
          );
          setLoading(false);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, elementID, view]);

  useEffect(() => {
    if (updateResourceObligationTab === elementID) loadResourceObligations();
  }, [updateResourceObligationTab]);
  useEffect(() => {
    loadResourceObligations();
  }, []);
  // < LOAD OBLIGATION TAB

  // > SELECT ALL RESOURCES
  const selectAll = useCallback(() => {
    dispatch(
      currentPageAction.change(
        'checkedResources',
        {
          [elementID]: _.map(resourceObligations, 'id'),
        },
        true,
      ),
    );
  }, [resourceObligations]);
  // < SELECT ALL RESOURCES

  // > UNSELECT ALL RESOURCES
  const unselectAll = useCallback(() => {
    dispatch(
      currentPageAction.change(
        'checkedResources',
        {
          [elementID]: [],
        },
        true,
      ),
    );
  }, []);
  // < UNSELECT ALL RESOURCES

  if (loading) return <AppNoInfo />;

  return (
    <Container className="p-3">
      {editMode ? (
        <ObligationResourceTabEditMode
          elementID={elementID}
          setEditMode={setEditMode}
        />
      ) : (
        <>
          {view === 'resource' &&
            (userBridge?.permissions?.level === 0 ||
              (userBridge?.permissions?.level === 3 &&
                userBridge?.permissions?.obligationResourceUpdate)) && (
              <div className="d-flex justify-content-end">
                <span
                  className="btn btn-xs btn-outline-light mb-2"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.edition-mode"
                    />,
                  )}
                  onClick={() => setEditMode(true)}
                >
                  <AppRemixIcon icon="edit" />
                </span>
              </div>
            )}
          {view === 'obligation' && (
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-xs btn-outline-light mr-2"
                onClick={selectAll}
                disabled={resourceObligations?.length === 0}
              >
                <span className="d-inline-flex">
                  <AppRemixIcon icon="checkbox" className="mr-1" />
                  <Translator
                    file="module-resourceManager"
                    name="resource.select.all.2"
                  />
                </span>
              </button>
              <button
                type="button"
                className="btn btn-xs btn-outline-light mr-2"
                onClick={unselectAll}
                disabled={resourceObligations?.length === 0}
              >
                <span className="d-inline-flex">
                  <AppRemixIcon icon="checkbox-blank" className="mr-1" />
                  <Translator
                    file="module-resourceManager"
                    name="resource.deselect.all"
                  />
                </span>
              </button>
              <MassiveObligationNextDateSettings
                elementID={elementID}
                loadObligationTab={loadResourceObligations}
              />
              <MassiveEvidenceEditForm elementID={elementID} />
            </div>
          )}
          <List $view={view} className="list-group">
            {resourceObligations !== undefined &&
              resourceObligations?.map((ro, i) => (
                <React.Fragment key={ro.id}>
                  {view === 'obligation' && (
                    <PreResource>
                      <ResourceCheckbox
                        elementID={elementID}
                        resourceObligationID={ro?.id}
                      />
                    </PreResource>
                  )}
                  <ObligationCard
                    key={ro.id}
                    resourceObligationIndex={i}
                    resourceObligation={ro}
                    elementID={elementID}
                    view={view}
                    obligationIDsInPlant={obligationIDsInPlant}
                    loadResourceObligationTab={loadResourceObligations}
                  />
                </React.Fragment>
              ))}
          </List>
          {resourceObligations?.length === 0 && (
            <div className="alert alert-info">
              <Translator file="module-legalCompliance" name="no.obligations" />
            </div>
          )}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
`;

const List = styled.ul`
  display: ${(props) => (props.$view === 'obligation' ? 'grid' : 'block')};
  grid-template-columns: 24px 1fr;
  padding-bottom: 10px;
`;

const PreResource = styled.div`
  display: flex;
  align-items: center;
`;
